// fetchAllRows / fetchAllRpcRows — completeness-asserting pagination helpers.
//
// Why this exists: hosted PostgREST clips every response at max_rows (1000 on
// this project, confirmed from the dashboard 2026-07-26) and returns HTTP 200
// regardless — a `.range()` loop that stops on "short page = last page" is
// structurally unable to tell a real end-of-data from a page the server
// capped below what was requested (see docs/audits/2026-07-25-silent-caps-audit.md
// and docs/plans/2026-07-25-silent-caps-remediation.md). Per house rules,
// partial data IS a failure: this helper throws (IncompleteResultError)
// instead of silently returning a truncated array.
//
// Deterministic order is mandatory — without it, "which rows are in this
// page" isn't even well-defined across separate `.range()` requests, so the
// completeness assertion below would be checking a meaningless total.

/** Thrown when a paginated fetch could not be proven complete: either the
 * accumulated row count never matched the server-declared count, or a page
 * came back short of the requested page size before that count was reached
 * (a strong signal the server capped the page below `pageSize`). Named and
 * greppable on purpose — never catch this and fall back to the partial data. */
export class IncompleteResultError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IncompleteResultError';
  }
}

/** Hosted PostgREST max_rows, confirmed 2026-07-26. This is the default page
 * size — callers may pass a smaller `pageSize` (e.g. for `.in()` chunking
 * headroom) but never a larger one, since that would just get clipped. */
export const DEFAULT_PAGE_SIZE = 1000;

export interface OrderSpec {
  column: string;
  options?: {
    ascending?: boolean;
    nullsFirst?: boolean;
    foreignTable?: string;
    referencedTable?: string;
  };
}

interface PageResponse<T> {
  data: T[] | null;
  error: { message: string; code?: string } | null;
  count?: number | null;
}

/** Structural (duck-typed) subset of PostgrestFilterBuilder/PostgrestTransformBuilder
 * that fetchAllRows needs — `.order()` chains, `.range()` executes. Matches the
 * real supabase-js builder without depending on its (transitive, not a direct
 * dependency) generic types. */
export interface PageableQuery<T> {
  order(column: string, options?: OrderSpec['options']): PageableQuery<T>;
  range(from: number, to: number): PromiseLike<PageResponse<T>>;
}

function applyOrder<T>(query: PageableQuery<T>, orderBy: OrderSpec | OrderSpec[]): PageableQuery<T> {
  const specs = Array.isArray(orderBy) ? orderBy : [orderBy];
  if (specs.length === 0) {
    throw new Error('fetchAllRows: orderBy must include at least one column — deterministic order is mandatory.');
  }
  return specs.reduce((q, spec) => q.order(spec.column, spec.options), query);
}

/**
 * Pages a `.range()`-based supabase-js query to completion and asserts it.
 *
 * `buildQuery` must return a fresh, unordered query each call, with its
 * `.select(...)` already requesting `{ count: 'exact' }` — this helper reads
 * `count` off the first page's response and throws if it's missing (a
 * `count: 'exact'` the caller forgot) or if the final accumulated length
 * doesn't match it.
 *
 * Fails loudly on any page that comes back shorter than `pageSize` before the
 * declared count is reached — that shape is the fingerprint of a server-side
 * cap below `pageSize` (e.g. max_rows lowered under this helper), not a
 * legitimate final page.
 *
 * `orderBy` MUST resolve to a unique key — a single non-unique column, or a
 * column list that still allows ties, is not enough. Postgres does not
 * guarantee stable tie-order across separate scans, and separate `.range()`
 * calls are separate scans: a non-unique order can skip or duplicate rows
 * across a page boundary while the accumulated length still lands on the
 * declared count by coincidence, which the completeness assertion above
 * cannot detect (it checks length, not identity). Add a tiebreaker column
 * (typically `id`) whenever the natural sort key isn't already unique.
 */
export async function fetchAllRows<T>(
  buildQuery: () => PageableQuery<T>,
  orderBy: OrderSpec | OrderSpec[],
  options: { pageSize?: number } = {},
): Promise<T[]> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const out: T[] = [];
  let declaredCount: number | null = null;

  for (let from = 0; ; from += pageSize) {
    const query = applyOrder(buildQuery(), orderBy);
    const { data, error, count } = await query.range(from, from + pageSize - 1);
    // Keep the PostgREST/Postgres error code on the thrown Error so react-query's
    // retry policy (src/lib/queryRetry.ts) can recognise a deterministic 42501.
    if (error) throw Object.assign(new Error(`fetchAllRows: ${error.message}`), { code: error.code });
    const rows = data ?? [];

    if (from === 0) {
      if (count == null) {
        throw new Error(
          "fetchAllRows: no count returned on the first page — buildQuery's .select() must request { count: 'exact' }.",
        );
      }
      declaredCount = count;
    }

    out.push(...rows);

    if (rows.length < pageSize) {
      if (out.length !== declaredCount) {
        throw new IncompleteResultError(
          `fetchAllRows: short page (${rows.length} rows, expected ${pageSize}) at offset ${from} — ` +
            `received ${out.length} of ${declaredCount} declared rows. The server likely capped this page ` +
            `below the requested page size (e.g. a lower max_rows than this helper was configured for).`,
        );
      }
      break;
    }
  }

  if (out.length !== declaredCount) {
    throw new IncompleteResultError(
      `fetchAllRows: paginated to completion but accumulated ${out.length} rows against a declared count of ${declaredCount}.`,
    );
  }

  return out;
}

/** Hard cap on pages for fetchAllRpcRows — a runaway loop (e.g. a query that
 * genuinely never returns fewer than pageSize rows) fails loudly instead of
 * paginating forever. 50 pages x 1000 rows = 50,000 rows, well above any real
 * RPC result set on this project as of 2026-07. */
const DEFAULT_MAX_PAGES = 50;

/**
 * RPC variant of fetchAllRows for SETOF-returning Postgres functions.
 *
 * Honest limitation, documented rather than hidden: PostgREST does not
 * return a `count` for RPC calls the way it does for table/view selects, so
 * this variant has no server-declared total to assert against. It cannot
 * distinguish "the underlying result legitimately ends here" from "the
 * function's result changed between two of our page requests" (a race, not
 * a cap issue — pagination here is multiple independent HTTP calls, not a
 * snapshot). What it CAN and does guarantee: it never treats a short-but-
 * non-empty page as the end (the bug class in this audit) — it advances the
 * offset by the number of rows actually received and only stops on a
 * genuinely empty page, so a server cap below `pageSize` costs extra round
 * trips, not missing rows. A `maxPages` ceiling still fails loudly instead
 * of looping forever if the source is somehow unbounded.
 *
 * `orderBy` MUST resolve to a unique key, same requirement as fetchAllRows
 * and for the same reason: a non-unique order lets separate `.range()` scans
 * disagree on tie order, which can skip or duplicate rows across a page
 * boundary. This variant has no count to catch that even coincidentally —
 * add a tiebreaker column (typically `id`) whenever the natural sort key
 * isn't already unique.
 */
export async function fetchAllRpcRows<T>(
  buildQuery: () => PageableQuery<T>,
  orderBy: OrderSpec | OrderSpec[],
  options: { pageSize?: number; maxPages?: number } = {},
): Promise<T[]> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const maxPages = options.maxPages ?? DEFAULT_MAX_PAGES;
  const out: T[] = [];

  for (let page = 0, offset = 0; ; page += 1) {
    if (page >= maxPages) {
      throw new Error(
        `fetchAllRpcRows: exceeded ${maxPages} pages (${maxPages * pageSize} rows) — aborting instead of looping forever.`,
      );
    }
    const query = applyOrder(buildQuery(), orderBy);
    const { data, error } = await query.range(offset, offset + pageSize - 1);
    if (error) throw Object.assign(new Error(`fetchAllRpcRows: ${error.message}`), { code: error.code });
    const rows = data ?? [];
    if (rows.length === 0) break;
    out.push(...rows);
    offset += rows.length;
  }

  return out;
}
